// src/components/lending/LenderDashboard.js
import React, { useEffect, useState } from 'react';
import { reportService } from '../../services/reportService';
import { useAuth } from '../../context/AuthContext';
import { ReportStatus } from '../constants'; 
import ReportCard from './ReportCard';
import BorrowerClaimCard from '../borrowing/BorrowerClaimCard';
import { Link, useNavigate } from 'react-router-dom';
import { 
  PlusCircle, RefreshCw, ShieldAlert, FileText, Wallet, User, 
  Loader2, AlertCircle, TrendingUp, Clock, MessageSquare, CheckCircle
} from 'lucide-react';

const Dashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [myReports, setMyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('LENDER');
  
  const [lenderCount, setLenderCount] = useState(0);
  const [borrowerCount, setBorrowerCount] = useState(0);
  const [urgentCount, setUrgentCount] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    loadData();
  }, [currentUser, viewMode, navigate]);

  const loadData = async (isRefresh = false) => {
    if (!currentUser || !userProfile) return;

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      let activeMode = viewMode;
      if (!userProfile.roles?.includes(activeMode)) {
        if (userProfile.roles?.includes('LENDER')) {
          activeMode = 'LENDER';
        } else if (userProfile.roles?.includes('BORROWER')) {
          activeMode = 'BORROWER';
        }
        setViewMode(activeMode);
      }

      const [lenderReportsData, borrowerReportsData] = await Promise.all([
        userProfile.roles?.includes('LENDER') 
          ? reportService.getReportsByLender(currentUser.uid)
          : Promise.resolve([]),
        userProfile.roles?.includes('BORROWER') && userProfile.mobile
          ? reportService.getReportsByBorrower(userProfile.mobile)
          : Promise.resolve([])
      ]);

      setLenderCount(lenderReportsData.length);
      setBorrowerCount(borrowerReportsData.length);
      
      // Count urgent claims (pending response without borrower reply)
      const urgent = borrowerReportsData.filter(r => 
        !r.borrowerResponse && 
        (r.status === ReportStatus.PENDING_RESPONSE || r.status === ReportStatus.PUBLISHED_UNRESOLVED)
      ).length;
      setUrgentCount(urgent);

      if (activeMode === 'BORROWER') {
        setMyReports(borrowerReportsData);
      } else {
        setMyReports(lenderReportsData);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadData(true);
  };

  const handleReportUpdate = () => {
    loadData(true);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-emerald-500 mx-auto mb-4" size={48} />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <p className="text-slate-400">Unable to load profile</p>
        </div>
      </div>
    );
  }

  const showToggle = userProfile.roles?.includes('LENDER') && userProfile.roles?.includes('BORROWER');

  const stats = viewMode === 'BORROWER' ? {
    total: myReports.length,
    urgent: myReports.filter(r => !r.borrowerResponse && r.status !== ReportStatus.PUBLISHED_SETTLED).length,
    disputed: myReports.filter(r => r.status === ReportStatus.PUBLISHED_DISPUTED).length,
    settled: myReports.filter(r => r.status === ReportStatus.PUBLISHED_SETTLED).length,
  } : {
    total: myReports.length,
    disputed: myReports.filter(r => r.status === ReportStatus.PUBLISHED_DISPUTED).length,
    settled: myReports.filter(r => r.status === ReportStatus.PUBLISHED_SETTLED).length,
    pending: myReports.filter(r => r.status === ReportStatus.PENDING_RESPONSE).length,
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      
      {/* View Toggle for Multi-Role Users */}
      {showToggle && (
        <div className="flex justify-center mb-8 animate-in slide-in-from-top-4">
          <div className="bg-slate-900 p-1 rounded-xl border border-slate-700 flex gap-1 shadow-lg">
            <button
              onClick={() => setViewMode('LENDER')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
                viewMode === 'LENDER' 
                  ? 'bg-emerald-600 text-white shadow-md' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Wallet size={16} /> 
              <span>Lender View</span>
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                viewMode === 'LENDER' 
                  ? 'bg-emerald-800 text-emerald-100' 
                  : 'bg-slate-800 text-slate-500'
              }`}>
                {lenderCount}
              </span>
            </button>
            <button
              onClick={() => setViewMode('BORROWER')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all relative ${
                viewMode === 'BORROWER' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <User size={16} /> 
              <span>Borrower View</span>
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                viewMode === 'BORROWER' 
                  ? 'bg-blue-800 text-blue-100' 
                  : 'bg-slate-800 text-slate-500'
              }`}>
                {borrowerCount}
              </span>
              {urgentCount > 0 && viewMode !== 'BORROWER' && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse">
                  {urgentCount}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Urgent Alert Banner for Borrowers */}
      {viewMode === 'BORROWER' && stats.urgent > 0 && (
        <div className="mb-6 bg-red-900/30 border border-red-500/50 rounded-xl p-4 animate-in slide-in-from-top-2">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-500/20 p-2 rounded-lg">
                <AlertCircle className="text-red-400" size={24} />
              </div>
              <div>
                <p className="text-red-400 font-bold">Action Required</p>
                <p className="text-sm text-slate-400">
                  You have {stats.urgent} claim{stats.urgent > 1 ? 's' : ''} awaiting your response
                </p>
              </div>
            </div>
            <a 
              href="#urgent-claims"
              className="bg-red-500 hover:bg-red-400 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors"
            >
              View Claims
            </a>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-white">
              {viewMode === 'BORROWER' ? 'Claims Against Me' : 'My Submitted Reports'}
            </h1>
            {viewMode === 'BORROWER' && (
              <span className="bg-blue-900/30 text-blue-400 border border-blue-500/30 text-xs px-2 py-1 rounded font-bold uppercase tracking-wider">
                Borrower Dashboard
              </span>
            )}
            {viewMode === 'LENDER' && (
              <span className="bg-emerald-900/30 text-emerald-400 border border-emerald-500/30 text-xs px-2 py-1 rounded font-bold uppercase tracking-wider">
                Lender Dashboard
              </span>
            )}
          </div>
          <p className="text-slate-400 mt-1">
            {viewMode === 'BORROWER'
              ? `Tracking reports linked to your mobile: ${userProfile.mobile}` 
              : 'Manage and track the credit reports you have filed.'}
          </p>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg font-bold transition-colors border border-slate-700 disabled:opacity-50"
          >
            <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
          </button>
          {viewMode === 'LENDER' && (
            <Link 
              to="/list" 
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg font-bold transition-colors shadow-lg shadow-emerald-900/20"
            >
              <PlusCircle size={20} />
              Submit New Report
            </Link>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {viewMode === 'BORROWER' ? (
          <>
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 hover:border-slate-600 transition-colors">
              <span className="text-slate-400 text-xs uppercase font-bold flex items-center gap-2">
                <FileText size={14} />
                Total Claims
              </span>
              <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-xl border border-red-500/30 hover:border-red-500/50 transition-colors">
              <span className="text-slate-400 text-xs uppercase font-bold flex items-center gap-2">
                <Clock size={14} className="text-red-400" />
                Need Response
              </span>
              <p className="text-2xl font-bold text-red-400 mt-1">{stats.urgent}</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 hover:border-orange-500/30 transition-colors">
              <span className="text-slate-400 text-xs uppercase font-bold flex items-center gap-2">
                <MessageSquare size={14} />
                Disputed
              </span>
              <p className="text-2xl font-bold text-orange-400 mt-1">{stats.disputed}</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 hover:border-emerald-500/30 transition-colors">
              <span className="text-slate-400 text-xs uppercase font-bold flex items-center gap-2">
                <CheckCircle size={14} />
                Settled
              </span>
              <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.settled}</p>
            </div>
          </>
        ) : (
          <>
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 hover:border-slate-600 transition-colors">
              <span className="text-slate-400 text-xs uppercase font-bold flex items-center gap-2">
                <FileText size={14} />
                Total Reports
              </span>
              <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 hover:border-orange-500/30 transition-colors">
              <span className="text-slate-400 text-xs uppercase font-bold flex items-center gap-2">
                <TrendingUp size={14} />
                Active Disputes
              </span>
              <p className="text-2xl font-bold text-orange-400 mt-1">{stats.disputed}</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 hover:border-emerald-500/30 transition-colors">
              <span className="text-slate-400 text-xs uppercase font-bold flex items-center gap-2">
                <ShieldAlert size={14} />
                Settled
              </span>
              <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.settled}</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 hover:border-yellow-500/30 transition-colors">
              <span className="text-slate-400 text-xs uppercase font-bold">Pending</span>
              <p className="text-2xl font-bold text-yellow-400 mt-1">{stats.pending}</p>
            </div>
          </>
        )}
      </div>

      {/* Reports List Header */}
      <h2 id="urgent-claims" className="text-xl font-bold text-slate-200 mb-4 flex items-center gap-2">
        {viewMode === 'BORROWER' ? (
          <>
            <User size={20} className="text-blue-400" />
            Claims to Review
          </>
        ) : (
          <>
            <FileText size={20} className="text-emerald-400" />
            Submission History
          </>
        )}
      </h2>
      
      {/* Reports List */}
      {myReports.length === 0 ? (
        <div className="text-center py-20 bg-slate-900/50 rounded-2xl border border-slate-800 border-dashed">
          <div className="bg-slate-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            {viewMode === 'BORROWER' ? (
              <ShieldAlert className="text-emerald-500" size={32} />
            ) : (
              <FileText className="text-slate-500" size={32} />
            )}
          </div>
          
          {viewMode === 'BORROWER' ? (
            <>
              <h3 className="text-xl font-bold text-white mb-2">No Claims Found</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Good news! We couldn't find any credit reports linked to{' '}
                <strong className="text-slate-400">{userProfile.mobile}</strong>.
              </p>
              <p className="text-xs text-slate-600 mt-4">
                If you believe this is an error, ensure your registered mobile number matches the one used by the lender.
              </p>
            </>
          ) : (
            <>
              <h3 className="text-xl font-bold text-white mb-2">Get Started</h3>
              <p className="text-slate-500 mb-4">You haven't submitted any reports yet.</p>
              <Link 
                to="/list" 
                className="inline-flex items-center gap-2 text-emerald-500 font-bold hover:text-emerald-400 transition-colors"
              >
                <PlusCircle size={18} />
                Submit your first report
              </Link>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {viewMode === 'BORROWER' ? (
            // Borrower view - sorted by urgency
            [...myReports]
              .sort((a, b) => {
                // Urgent first (no response + not settled)
                const aUrgent = !a.borrowerResponse && a.status !== ReportStatus.PUBLISHED_SETTLED ? 0 : 1;
                const bUrgent = !b.borrowerResponse && b.status !== ReportStatus.PUBLISHED_SETTLED ? 0 : 1;
                if (aUrgent !== bUrgent) return aUrgent - bUrgent;
                // Then by date
                const aDate = a.createdDate?.seconds || new Date(a.createdDate).getTime() / 1000;
                const bDate = b.createdDate?.seconds || new Date(b.createdDate).getTime() / 1000;
                return bDate - aDate;
              })
              .map(report => (
                <BorrowerClaimCard 
                  key={report.id} 
                  report={report} 
                  onUpdate={handleReportUpdate}
                />
              ))
          ) : (
            // Lender view
            myReports.map(report => (
              <ReportCard 
                key={report.id} 
                report={report} 
                isPublic={true} 
                isOwner={true}
                onUpdate={handleReportUpdate}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;